import type { ContentBlock, ContentStructure } from "../types/kbContentStructure";
import { SCHEMA_VERSION } from "../types/kbContentStructure";

const DECIMAL_RE = /^\s*(\d+)[.)]\s+(.+)$/;
const LETTER_RE = /^\s*([a-zA-Z])[.)]\s+(.+)$/;
const BULLET_RE = /^\s*[•\-\*●○]\s+(.+)$/;
const SECTION_LEAD_RE = /^([A-Za-z][^:]{2,60}):\s*(.*)$/;

/**
 * Best-effort structure from plain text when Word OOXML capture is unavailable.
 * Mirrors backend app.utils.kb_structure_plain.plain_text_to_structure.
 */
export const plainTextToStructure = (
  text: string,
  sectionName?: string,
): ContentStructure => {
  const lines = (text || "").split(/\r?\n/).map((ln) => ln.replace(/\s+$/, ""));
  const blocks: ContentBlock[] = [];
  let decimalItems: string[] = [];
  let letterItems: string[] = [];
  let bulletItems: string[] = [];

  const flushDecimal = (): void => {
    if (decimalItems.length > 0) {
      blocks.push({ type: "numbered_list", scheme: "decimal", items: [...decimalItems] });
      decimalItems = [];
    }
  };
  const flushLetter = (): void => {
    if (letterItems.length > 0) {
      blocks.push({
        type: "numbered_list",
        scheme: "lowerLetter",
        style: "BodyText",
        items: [...letterItems],
      });
      letterItems = [];
    }
  };
  const flushBullet = (): void => {
    if (bulletItems.length > 0) {
      blocks.push({
        type: "bullet_list",
        items: bulletItems.map((t) => ({ text: t, children: [] })),
      });
      bulletItems = [];
    }
  };

  for (const line of lines) {
    const stripped = line.trim();
    if (!stripped) {
      flushDecimal();
      flushLetter();
      flushBullet();
      continue;
    }

    const dec = DECIMAL_RE.exec(stripped);
    if (dec) {
      flushLetter();
      flushBullet();
      decimalItems.push(dec[2].trim());
      continue;
    }

    const letter = LETTER_RE.exec(stripped);
    if (letter) {
      flushDecimal();
      flushBullet();
      letterItems.push(letter[2].trim());
      continue;
    }

    const bullet = BULLET_RE.exec(stripped);
    if (bullet) {
      flushDecimal();
      flushLetter();
      bulletItems.push(bullet[1].trim());
      continue;
    }

    flushDecimal();
    flushLetter();
    flushBullet();

    const lead = SECTION_LEAD_RE.exec(stripped);
    if (lead) {
      blocks.push({
        type: "section_lead",
        label: `${lead[1]}:`,
        text: lead[2].trim(),
      });
    } else {
      blocks.push({ type: "paragraph", text: stripped, align: "both" });
    }
  }

  flushDecimal();
  flushLetter();
  flushBullet();

  if (blocks.length === 0 && (text || "").trim()) {
    blocks.push({ type: "paragraph", text: text.trim(), align: "both" });
  } else if (blocks.length === 0 && sectionName) {
    blocks.push({ type: "paragraph", text: "", align: "both" });
  }

  return { schema_version: SCHEMA_VERSION, blocks };
};
